import { Switch, Route, useLocation } from "wouter";
import React from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
// useBusinesses removed — admin pages must not trigger business hooks
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LoadingProvider } from "@/providers/loading-provider";
import NotFound from "@/pages/not-found";
import LandingPage from "@/pages/landing";
import AboutPage from "@/pages/about";
import ContactPage from "@/pages/contact";
import FeaturesPage from "@/pages/features";
import PricingPage from "@/pages/pricing";
import BenefitsPage from "@/pages/benefits";
import HowItWorksPage from "@/pages/how-it-works";
import VerificationPage from "@/pages/auth/verification";
import OnboardingDemoPage from "@/pages/onboarding-demo";
import DirectOnboardingPage from "@/pages/direct-onboarding";
import AuthPage from "@/pages/auth";
import LoginPage from "@/pages/auth/login";
import SignupPage from "@/pages/auth/signup";
import OnboardingPage from "@/pages/auth/onboarding";
import ForgotPasswordPage from "@/pages/auth/forgot-password";
import ResetPasswordPage from "@/pages/auth/reset-password";
import PlanSelectionPage from "@/pages/subscription/plan-selection";
import TestLoginPage from "@/pages/test-login";
import SubscriptionPlansPage from "@/pages/subscription/plans";
import ManageSubscriptionPlansPage from "@/pages/subscription/manage-plans";
import AdminSubscriptionPlansPage from "@/pages/admin/subscription-plans";
import NewSubscriptionPlansPage from "@/pages/admin/new-subscription-plans";
import SimpleSubscriptionPlansPage from "@/pages/admin/simple-subscription-plans";
import WorkingSubscriptionPlansPage from "@/pages/admin/working-subscription-plans";
import FriendlySubscriptionPlansPage from "@/pages/admin/friendly-subscription-plans";
import CustomerSubscriptionsPage from "@/pages/admin/customer-subscriptions";
import DashboardPage from "@/pages/dashboard";
import TransactionsPage from "@/pages/transactions";
import TransactionRegisterPage from "@/pages/transaction-register";
import BankReconciliationPage from "@/pages/bank-reconciliation";
import ReportsPage from "@/pages/reports-final";
import EnhancedReportsPage from "@/pages/reports-new";
import ProfitLossReport from "@/pages/profit-loss";
import BalanceSheetReport from "@/pages/balance-sheet";
import JasperReportsPage from "@/pages/reports/jasper-reports";
import TasksPage from "@/pages/tasks";
import ChatPage from "@/pages/chat";
import SettingsPage from "@/pages/settings";
import DocumentsPage from "@/pages/documents";
import AccountsPage from "@/pages/accounts";
import CreateBusinessPageSimple from "@/pages/business/create-simple";
import BusinessProfilePage from "@/pages/business/profile";
import EditBusinessPage from "@/pages/business/edit";
// Import sales pages
import CustomersPage from "@/pages/sales/customers";
import ProductsPage from "@/pages/sales/products";
import InvoicesPage from "@/pages/sales/invoices";
import EstimatesPage from "@/pages/sales/estimates";
import SalesTaxPage from "@/pages/sales/sales-tax";
// Import expense pages
import BillsPage from "@/pages/expenses/bills";
import BillPaymentsPage from "@/pages/expenses/bill-payments";
import VendorsPage from "@/pages/expenses/vendors";
import ContractorsPage from "@/pages/expenses/contractors";
import ContractorDetailsPage from "@/pages/expenses/contractor-details";
import ExpensesPage from "@/pages/expenses/expenses";
import ExpensesCreatePage from "@/pages/expenses/create";
import ExpensesEditPage from "@/pages/expenses/edit";
import TaxFilingsPage from "@/pages/expenses/tax-filings-new";
import TaxResearchPage from "@/pages/tax-research";
import TimezoneSettings from "@/pages/settings/timezone";
// Import legal document pages
import LegalDocumentsPage from "@/pages/legal/index";
import CreateLegalDocumentPage from "@/pages/legal/create/[type]";
import ViewLegalDocumentPage from "@/pages/legal/view/[id]";
// Import direct accounts access page
import DirectAccountsPage from "@/pages/direct-accounts";
// Import admin pages
import ClientManagementPage from "@/pages/admin/client-management";
import AdminDashboard from "@/pages/admin/dashboard";
import AdminSupportPage from "@/pages/admin/support";
import CompliancePage from "@/pages/admin/compliance";
// Import inventory pages
import InventoryDashboard from "@/pages/inventory/dashboard";
import InventoryItems from "@/pages/inventory/items";
// Import budgeting pages
import BudgetsPage from "@/pages/budgets/index";
import AIForecastingDashboard from "@/pages/budgets/ai-forecasting";
// Import cash flow pages
import CashFlowProjections from "@/pages/cash-flow/projections";
// Import tax reminders page
import TaxReminders from "@/pages/tax-reminders";
// Import payroll integration page
import PayrollIntegration from "@/pages/payroll";
// Import subscription page
import CustomerSubscription from "@/pages/subscription";
// Import advanced reports page
import AdvancedReportsPage from "@/pages/reports/advanced";
// Import product edit page
import ProductEditPage from "@/pages/sales/product-edit";
// Import profile page
import ProfilePage from "@/pages/profile";
// Import AI Assistant page
import AIAssistantPage from "@/pages/ai-assistant";
// Import announcements admin page
import AnnouncementsAdmin from "@/pages/zentro-admin/announcements";
import AdminCoaTemplatesPage from "@/pages/admin/coa-templates";
import RulesPage from "@/pages/bookkeeping/rules";
import StaffListPage from "@/pages/staff";
import StaffProfilePage from "@/pages/staff/profile";
import StaffLoginPage from "@/pages/staff-portal/login";
import StaffDashboardPage from "@/pages/staff-portal/dashboard";
import AIBookkeeperPage from "@/pages/ai-bookkeeper";
import ClientAIDashboard from "@/pages/client-ai-dashboard";
import TrialBalancePage from "@/pages/reports/trial-balance";
import GeneralLedgerPage from "@/pages/reports/general-ledger";
import ProfitLossV2Page from "@/pages/reports/profit-loss";
import JournalEntriesPage from "@/pages/journal-entries";
import ReceiptsPage from "@/pages/bookkeeping/receipts";
import ClientDashboardPage from "@/pages/client-dashboard";
// New subscription/onboarding pages
import PricingPlansPage from "@/pages/subscription/pricing-plans";
import SubscriptionSuccessPage from "@/pages/subscription/success";
import ConnectBankPage from "@/pages/connect-bank";
import BillingPage from "@/pages/settings/billing";
import LicensesPage from "@/pages/settings/licenses";
import OnboardingSetup from "@/pages/onboarding/setup";
import OnboardingWizardPage from "@/pages/onboarding-wizard";
// Import simplified reports page
import ReportsSimplePage from "@/pages/reports-simple";
// Import comprehensive reports dashboard
import ReportsDashboardPage from "@/pages/reports-dashboard";
import { useAuth } from "@/providers/auth-provider";
import { AuthProvider } from "@/providers/auth-provider";
import { BusinessProvider, useBusinessContext } from "@/providers/business-provider";
import { Loader2 } from "lucide-react";
import { PermissionGuard } from "@/components/permission-guard";
import { canAccessRoute } from "@/lib/permissions";

// Helper — is the current user the platform admin?
// ONLY hello@zentrobooks.com. No other account.
function isPlatformAdmin(user: any): boolean {
  if (!user) return false;
  return user.email === "hello@zentrobooks.com";
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const [location, navigate] = useLocation();

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!isLoading && !user) {
      navigate("/auth/login");
    }
  }, [user, isLoading, navigate, location]);

  // ── Role-based routing ──────────────────────────────────────────
  React.useEffect(() => {
    if (!user) return;
    const admin = isPlatformAdmin(user);
    console.log(`[AUTH] user=${user.email} isPlatformAdmin=${admin} location=${location}`);

    // Platform admin on tenant app → redirect to admin dashboard
    if (admin && !location.startsWith("/admin") && !location.startsWith("/auth") && !location.startsWith("/profile")) {
      console.log("[ROUTE] Platform admin → /admin/dashboard");
      navigate("/admin/dashboard");
      return;
    }

    // Tenant owner on admin routes → redirect to tenant dashboard
    if (!admin && location.startsWith("/admin")) {
      console.log("[ROUTE] Tenant owner → /dashboard (blocked from /admin)");
      navigate("/dashboard");
      return;
    }
  }, [user, location, navigate]);

  // Onboarding gate — redirect to /onboarding if user hasn't completed setup
  // (skip for admin and staff — neither goes through onboarding)
  const { data: userData } = useQuery({
    queryKey: ["/api/user"],
    enabled: !!user,
    staleTime: 60000,
  });
  React.useEffect(() => {
    if (!user || isPlatformAdmin(user) || (user as any).isStaff) return;
    if (userData && (userData as any).hasCompletedOnboarding === false &&
        !location.startsWith("/onboarding") && !location.startsWith("/auth")) {
      navigate("/onboarding");
    }
  }, [(userData as any)?.hasCompletedOnboarding, location, navigate, user]);

  // Trial expiry gate — force expired trial users to subscription page
  // (skip for admin and staff — staff cannot manage billing)
  const { data: trialGate } = useTrialGate();
  React.useEffect(() => {
    if (!user || isPlatformAdmin(user) || (user as any).isStaff) return;
    if (trialGate?.blocked && !location.startsWith("/subscription") && !location.startsWith("/profile") && !location.startsWith("/settings/licenses") && !location.startsWith("/auth") && !location.startsWith("/onboarding")) {
      navigate("/subscription");
    }
  }, [trialGate?.blocked, location, navigate, user]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return <>{children}</>;
}

// Hook to check trial expiry — used by ProtectedRoute to force redirect
// Uses context directly (no business fetch for admin since provider skips it)
function useTrialGate() {
  const { activeBusiness } = useBusinessContext();
  return useQuery({
    queryKey: ["/api/trial-gate", activeBusiness?.id],
    queryFn: async () => {
      if (!activeBusiness?.id) return { blocked: false };
      try {
        // Check subscription status first — if paid Stripe sub exists, never block
        const subRes = await fetch('/api/billing/subscription', { credentials: "include" });
        if (subRes.ok) {
          const sub = await subRes.json();
          // Failsafe: if stripeSubscriptionId exists and status is active, never block
          if (sub?.stripeSubscriptionId && sub.status === 'active') return { blocked: false };
          if (sub && (sub.status === "expired" || sub.status === "cancelled")) return { blocked: true };
        }
        // Check trial expiry only if no paid subscription
        const trialRes = await fetch(`/api/businesses/${activeBusiness.id}/trial-status`, { credentials: "include" });
        if (trialRes.ok) {
          const trial = await trialRes.json();
          if (trial.onTrial && trial.expired) return { blocked: true };
        }
        return { blocked: false };
      } catch {
        return { blocked: false };
      }
    },
    enabled: !!activeBusiness?.id,
    staleTime: 30000,
    refetchInterval: 60000,
  });
}

function Router() {
  // Debug - Log the current path
  const [location] = useLocation();
  console.log("Current location:", location);
  
  return (
    <Switch>
      {/* Root → Login */}
      <Route path="/" component={LoginPage} />

      {/* Auth Routes */}
      <Route path="/login" component={LoginPage} />
      <Route path="/signup" component={SignupPage} />
      <Route path="/auth" component={AuthPage} />
      <Route path="/auth/login" component={LoginPage} />
      <Route path="/auth/signup" component={SignupPage} />
      <Route path="/auth/register" component={SignupPage} />
      <Route path="/auth/onboarding" component={OnboardingPage} />
      <Route path="/auth/verification" component={VerificationPage} />
      <Route path="/auth/forgot-password" component={ForgotPasswordPage} />
      <Route path="/auth/reset-password" component={ResetPasswordPage} />
      <Route path="/connect-bank" component={ConnectBankPage} />
      <Route path="/subscription/success" component={SubscriptionSuccessPage} />
      <Route path="/subscription/plan-selection" component={PlanSelectionPage} />
      <Route path="/subscription/plans" component={SubscriptionPlansPage} />
      <Route path="/subscription/pricing" component={PricingPlansPage} />
      <Route path="/subscription/manage-plans">
        <ProtectedRoute>
          <ManageSubscriptionPlansPage />
        </ProtectedRoute>
      </Route>

      <Route path="/onboarding">
        <ProtectedRoute>
          <OnboardingSetup />
        </ProtectedRoute>
      </Route>

      <Route path="/settings/billing">
        <ProtectedRoute>
          <BillingPage />
        </ProtectedRoute>
      </Route>

      <Route path="/settings/licenses">
        <ProtectedRoute>
          <LicensesPage />
        </ProtectedRoute>
      </Route>

      {/* Protected Routes */}
      <Route path="/dashboard">
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      </Route>
      <Route path="/ai-assistant">
        <ProtectedRoute>
          <AIAssistantPage />
        </ProtectedRoute>
      </Route>
      <Route path="/transactions">
        <ProtectedRoute>
          <TransactionsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/transaction-register">
        <ProtectedRoute>
          <TransactionRegisterPage />
        </ProtectedRoute>
      </Route>
      <Route path="/bookkeeping/rules">
        <ProtectedRoute>
          <RulesPage />
        </ProtectedRoute>
      </Route>
      <Route path="/ai-bookkeeper">
        <ProtectedRoute>
          <AIBookkeeperPage />
        </ProtectedRoute>
      </Route>
      <Route path="/my-books">
        <ProtectedRoute>
          <ClientAIDashboard />
        </ProtectedRoute>
      </Route>
      <Route path="/client-dashboard">
        <ProtectedRoute>
          <ClientDashboardPage />
        </ProtectedRoute>
      </Route>
      <Route path="/bank-reconciliation">
        <ProtectedRoute>
          <BankReconciliationPage />
        </ProtectedRoute>
      </Route>
      <Route path="/reports">
        <ProtectedRoute>
          <ReportsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/reports-simple">
        <ProtectedRoute>
          <ReportsSimplePage />
        </ProtectedRoute>
      </Route>
      <Route path="/reports-dashboard">
        <ProtectedRoute>
          <ReportsDashboardPage />
        </ProtectedRoute>
      </Route>
      <Route path="/reports-new">
        <ProtectedRoute>
          <ReportsDashboardPage />
        </ProtectedRoute>
      </Route>
      <Route path="/reports/jasper">
        <ProtectedRoute>
          <PermissionGuard feature="canManageReports" redirect>
            <JasperReportsPage />
          </PermissionGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/jasper-reports">
        <ProtectedRoute>
          <PermissionGuard feature="canManageReports" redirect>
            <JasperReportsPage />
          </PermissionGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/profit-loss">
        <ProtectedRoute>
          <ProfitLossReport />
        </ProtectedRoute>
      </Route>
      <Route path="/balance-sheet">
        <ProtectedRoute>
          <BalanceSheetReport />
        </ProtectedRoute>
      </Route>
      <Route path="/tasks">
        <ProtectedRoute>
          <TasksPage />
        </ProtectedRoute>
      </Route>
      <Route path="/chat">
        <ProtectedRoute>
          <ChatPage />
        </ProtectedRoute>
      </Route>
      <Route path="/tax-reminders">
        <ProtectedRoute>
          <TaxReminders />
        </ProtectedRoute>
      </Route>
      <Route path="/payroll">
        <ProtectedRoute>
          <PayrollIntegration />
        </ProtectedRoute>
      </Route>
      <Route path="/reports/advanced">
        <ProtectedRoute>
          <PermissionGuard feature="canManageReports" redirect>
            <AdvancedReportsPage />
          </PermissionGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/reports/profit-loss">
        <ProtectedRoute>
          <PermissionGuard feature="canViewReports" redirect>
            <ProfitLossV2Page />
          </PermissionGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/reports/trial-balance">
        <ProtectedRoute>
          <PermissionGuard feature="canViewReports" redirect>
            <TrialBalancePage />
          </PermissionGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/reports/general-ledger">
        <ProtectedRoute>
          <PermissionGuard feature="canViewReports" redirect>
            <GeneralLedgerPage />
          </PermissionGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/journal-entries">
        <ProtectedRoute>
          <PermissionGuard feature="canManageTransactions" redirect>
            <JournalEntriesPage />
          </PermissionGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/bookkeeping/receipts">
        <ProtectedRoute>
          <PermissionGuard feature="canManageTransactions" redirect>
            <ReceiptsPage />
          </PermissionGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/profile">
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      </Route>
      <Route path="/settings">
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/staff">
        <ProtectedRoute>
          <StaffListPage />
        </ProtectedRoute>
      </Route>
      <Route path="/staff/:id/profile">
        {(params) => (
          <ProtectedRoute>
            <StaffProfilePage staffId={parseInt(params.id)} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/documents">
        <ProtectedRoute>
          <DocumentsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/accounts">
        <ProtectedRoute>
          <AccountsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/direct-accounts">
        <ProtectedRoute>
          <DirectAccountsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/business/create">
        <ProtectedRoute>
          <CreateBusinessPageSimple />
        </ProtectedRoute>
      </Route>
      <Route path="/business/profile">
        <ProtectedRoute>
          <BusinessProfilePage />
        </ProtectedRoute>
      </Route>
      <Route path="/business/:id/edit">
        {(params) => (
          <ProtectedRoute>
            <EditBusinessPage id={params.id} />
          </ProtectedRoute>
        )}
      </Route>
      
      {/* Sales Routes */}
      <Route path="/sales/customers">
        <ProtectedRoute>
          <CustomersPage />
        </ProtectedRoute>
      </Route>
      <Route path="/sales/customers/:id/invoices/new">
        {(params) => (
          <ProtectedRoute>
            <InvoicesPage customerId={params.id} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/sales/customers/:id/estimates/new">
        {(params) => (
          <ProtectedRoute>
            <EstimatesPage customerId={params.id} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/sales/products">
        <ProtectedRoute>
          <ProductsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/sales/products/edit/:id">
        {(params) => (
          <ProtectedRoute>
            <ProductEditPage />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/sales/invoices">
        <ProtectedRoute>
          <InvoicesPage />
        </ProtectedRoute>
      </Route>
      <Route path="/sales/estimates">
        <ProtectedRoute>
          <EstimatesPage />
        </ProtectedRoute>
      </Route>
      <Route path="/sales/sales-tax">
        <ProtectedRoute>
          <SalesTaxPage />
        </ProtectedRoute>
      </Route>
      
      {/* Expenses Routes */}
      <Route path="/expenses/bills">
        <ProtectedRoute>
          <BillsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/expenses/vendors">
        <ProtectedRoute>
          <VendorsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/expenses/bill-payments">
        <ProtectedRoute>
          <BillPaymentsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/expenses/contractors">
        <ProtectedRoute>
          <ContractorsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/expenses/contractors/:id">
        {(params) => (
          <ProtectedRoute>
            <ContractorDetailsPage />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/expenses/expenses">
        <ProtectedRoute>
          <ExpensesPage />
        </ProtectedRoute>
      </Route>
      <Route path="/expenses/create">
        <ProtectedRoute>
          <ExpensesCreatePage />
        </ProtectedRoute>
      </Route>
      <Route path="/expenses/edit/:id">
        {(params) => (
          <ProtectedRoute>
            <ExpensesEditPage />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/expenses/tax-filings">
        <ProtectedRoute>
          <TaxFilingsPage />
        </ProtectedRoute>
      </Route>
      
      {/* AI Tools Routes */}
      <Route path="/tax-research">
        <ProtectedRoute>
          <TaxResearchPage />
        </ProtectedRoute>
      </Route>
      
      {/* Legal Document Routes */}
      <Route path="/legal">
        <ProtectedRoute>
          <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
            <LegalDocumentsPage />
          </React.Suspense>
        </ProtectedRoute>
      </Route>
      <Route path="/legal/create/:type">
        {(params) => (
          <ProtectedRoute>
            <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
              <CreateLegalDocumentPage />
            </React.Suspense>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/legal/view/:id">
        {(params) => (
          <ProtectedRoute>
            <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
              <ViewLegalDocumentPage />
            </React.Suspense>
          </ProtectedRoute>
        )}
      </Route>
      
      {/* Settings Routes */}
      <Route path="/settings/timezone/:businessId">
        <ProtectedRoute>
          <TimezoneSettings />
        </ProtectedRoute>
      </Route>
      
      {/* Admin Routes - Permission guarded */}
      <Route path="/admin/client-management">
        <ProtectedRoute>
          <PermissionGuard feature="canManageClients" redirect>
            <ClientManagementPage />
          </PermissionGuard>
        </ProtectedRoute>
      </Route>

      <Route path="/admin/support">
        <ProtectedRoute>
          <PermissionGuard feature="canAccessSupport" redirect>
            <AdminSupportPage />
          </PermissionGuard>
        </ProtectedRoute>
      </Route>

      <Route path="/admin/compliance">
        <ProtectedRoute>
          <PermissionGuard feature="canAccessCompliance" redirect>
            <CompliancePage />
          </PermissionGuard>
        </ProtectedRoute>
      </Route>

      {/* Inventory Routes */}
      <Route path="/inventory">
        <ProtectedRoute>
          <InventoryItems />
        </ProtectedRoute>
      </Route>
      <Route path="/inventory/dashboard">
        <ProtectedRoute>
          <InventoryDashboard />
        </ProtectedRoute>
      </Route>
      <Route path="/inventory/items">
        <ProtectedRoute>
          <InventoryItems />
        </ProtectedRoute>
      </Route>
      <Route path="/inventory/items/new">
        <ProtectedRoute>
          <InventoryItems />
        </ProtectedRoute>
      </Route>

      {/* AI-Powered Budgeting Routes */}
      <Route path="/budgets">
        <ProtectedRoute>
          <BudgetsPage />
        </ProtectedRoute>
      </Route>
      
      <Route path="/budgets/ai-forecasting">
        <ProtectedRoute>
          <AIForecastingDashboard />
        </ProtectedRoute>
      </Route>

      {/* AI-Powered Cash Flow Routes */}
      <Route path="/cash-flow/projections">
        <ProtectedRoute>
          <CashFlowProjections />
        </ProtectedRoute>
      </Route>

      <Route path="/dashboard/client-management">
        <ProtectedRoute>
          <PermissionGuard feature="canManageClients" redirect>
            <ClientManagementPage />
          </PermissionGuard>
        </ProtectedRoute>
      </Route>
      <Route path="/admin/dashboard">
        <ProtectedRoute>
          <PermissionGuard feature="canAccessAdminDashboard" redirect>
            <AdminDashboard />
          </PermissionGuard>
        </ProtectedRoute>
      </Route>

      <Route path="/zentro-admin/announcements">
        <ProtectedRoute>
          <PermissionGuard feature="canManageAnnouncements" redirect>
            <AnnouncementsAdmin />
          </PermissionGuard>
        </ProtectedRoute>
      </Route>

      <Route path="/admin/coa-templates">
        <ProtectedRoute>
          <PermissionGuard feature="canAccessAdminDashboard" redirect>
            <AdminCoaTemplatesPage />
          </PermissionGuard>
        </ProtectedRoute>
      </Route>

      <Route path="/dashboard/subscription-plans">
        <ProtectedRoute>
          <PermissionGuard feature="canManageSubscriptions" redirect>
            <FriendlySubscriptionPlansPage />
          </PermissionGuard>
        </ProtectedRoute>
      </Route>

      <Route path="/dashboard/customer-subscriptions">
        <ProtectedRoute>
          <PermissionGuard feature="canManageSubscriptions" redirect>
            <CustomerSubscriptionsPage />
          </PermissionGuard>
        </ProtectedRoute>
      </Route>
      
      <Route path="/dashboard/subscription">
        <ProtectedRoute>
          <CustomerSubscription />
        </ProtectedRoute>
      </Route>

      <Route path="/subscription">
        <ProtectedRoute>
          <CustomerSubscription />
        </ProtectedRoute>
      </Route>
      
      {/* Staff Portal Routes (no ProtectedRoute — uses its own auth) */}
      <Route path="/staff-portal/login" component={StaffLoginPage} />
      <Route path="/staff-portal/dashboard" component={StaffDashboardPage} />

      {/* Fallback to 404 */}
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <BusinessProvider>
            <LoadingProvider>
              <Toaster />
              <Router />
            </LoadingProvider>
          </BusinessProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
